import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Chat, Close, DeleteOutline, Person, Send } from '@material-ui/icons';
import PropTypes from 'prop-types';

import api from '~/services/api';

import * as S from './styles';

const INITIAL_MESSAGE = {
  id: 1,
  role: 'assistant',
  content: 'Olá! Sou o assistente do SCA+. Como posso ajudar?',
};

const HTTPS_URL_REGEX = /(https:\/\/[^\s<>]+)/gi;
const TRAILING_PUNCTUATION_REGEX = /[.,;:!?]+$/;

function getAssistantContent(data) {
  const response = data?.data || data;
  const backendMessages = Array.isArray(response?.messages)
    ? response.messages
    : [];
  const lastAssistantMessage = [...backendMessages]
    .reverse()
    .find(
      (message) =>
        message?.roleassistenteteomensagem === 'assistant' &&
        typeof message?.contentassistenteteomensagem === 'string' &&
        message.contentassistenteteomensagem.trim()
    );

  return lastAssistantMessage?.contentassistenteteomensagem.trim();
}

function getErrorMessage(error) {
  if (Array.isArray(error)) return error.join(' ');
  if (error instanceof Error) return error.message;

  return 'Não foi possível obter uma resposta neste momento.';
}

function renderMessageContent(content) {
  return content.split(HTTPS_URL_REGEX).map((part, index) => {
    if (!/^https:\/\//i.test(part)) return part;

    const trailingPunctuation =
      part.match(TRAILING_PUNCTUATION_REGEX)?.[0] || '';
    const url = trailingPunctuation
      ? part.slice(0, -trailingPunctuation.length)
      : part;
    let href;

    try {
      href = new URL(url).href;
    } catch {
      return part;
    }

    return (
      <React.Fragment key={`${url}-${index}`}>
        <S.MessageLink href={href} target="_blank" rel="noopener noreferrer">
          {url}
        </S.MessageLink>
        {trailingPunctuation}
      </React.Fragment>
    );
  });
}

export default function AssistantChat({ open, onClose }) {
  const messagesContainerRef = useRef(null);
  const nextMessageId = useRef(1);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const container = messagesContainerRef.current;

    if (!open || !container) return undefined;

    const updateScrollPosition = () => {
      container.scrollTop = container.scrollHeight;
    };

    updateScrollPosition();

    const animationFrame = window.requestAnimationFrame(updateScrollPosition);
    const drawerTransitionTimer = window.setTimeout(updateScrollPosition, 300);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(drawerTransitionTimer);
    };
  }, [loading, messages, open]);

  const createMessage = useCallback((role, content) => {
    nextMessageId.current += 1;

    return {
      id: nextMessageId.current,
      role,
      content,
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    let isActive = true;

    async function loadMessages() {
      try {
        setLoading(true);

        const { data } = await api.post('assistenteteo/carregarmsg');

        if (!isActive) return;

        const backendMessages = Array.isArray(data?.messages)
          ? data.messages
          : [];
        const loadedMessages = backendMessages
          .filter(
            (message) =>
              typeof message?.contentassistenteteomensagem === 'string' &&
              message.contentassistenteteomensagem.trim()
          )
          .map((message) => ({
            id: message.idassistenteteomensagem,
            role:
              message.roleassistenteteomensagem === 'user'
                ? 'user'
                : 'assistant',
            content: message.contentassistenteteomensagem,
            createdAt: message.datahoracadastroassistenteteomensagem,
          }));

        nextMessageId.current = loadedMessages.reduce(
          (largest, message) => Math.max(largest, Number(message.id) || 0),
          INITIAL_MESSAGE.id
        );

        setMessages(
          loadedMessages.length > 0 ? loadedMessages : [INITIAL_MESSAGE]
        );
      } catch (error) {
        if (!isActive) return;

        setMessages([
          INITIAL_MESSAGE,
          createMessage('assistant', getErrorMessage(error)),
        ]);
      } finally {
        if (isActive) setLoading(false);
      }
    }

    loadMessages();

    return () => {
      isActive = false;
    };
  }, [createMessage, open]);

  const handleClear = useCallback(async () => {
    if (clearing || loading) return;

    setClearing(true);

    try {
      await api.post('assistenteteo/limpezaconversa');

      setMessages([createMessage('assistant', INITIAL_MESSAGE.content)]);
      setInput('');
    } catch (error) {
      setMessages((current) => [
        ...current,
        createMessage(
          'assistant',
          `Não foi possível limpar a conversa. ${getErrorMessage(error)}`
        ),
      ]);
    } finally {
      setClearing(false);
    }
  }, [clearing, createMessage, loading]);

  const handleSend = useCallback(
    async (event) => {
      event.preventDefault();

      const content = input.trim();

      if (!content || loading) return;

      const userMessage = createMessage('user', content);
      const currentMessages = [...messages, userMessage];

      setMessages(currentMessages);
      setInput('');
      setLoading(true);

      try {
        const endpoint = 'assistenteteo/handlesend';
        // const endpoint = process.env.REACT_APP_CHAT_ENDPOINT;

        if (!endpoint) {
          throw new Error(
            'O serviço do assistente ainda não foi configurado. Defina REACT_APP_CHAT_ENDPOINT.'
          );
        }
        const { data } = await api.post(endpoint, {
          message: content,
        });

        const answer = getAssistantContent(data);

        if (!answer) {
          throw new Error(
            'O serviço do assistente retornou uma resposta vazia.'
          );
        }

        setMessages((current) => [
          ...current,
          createMessage('assistant', answer),
        ]);
      } catch (error) {
        setMessages((current) => [
          ...current,
          createMessage('assistant', getErrorMessage(error)),
        ]);
      } finally {
        setLoading(false);
      }
    },
    [createMessage, input, loading, messages]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        handleSend(event);
      }
    },
    [handleSend]
  );

  const handleInputChange = useCallback((event) => {
    setInput(event.target.value);
  }, []);

  return (
    <S.Container aria-label="Assistente virtual">
      <S.Header>
        <S.HeaderTitle>
          <Chat />
          <div>
            <Typography variant="h6">Assistente SCA+</Typography>
            <span>Converse com o assistente virtual</span>
          </div>
        </S.HeaderTitle>

        <S.HeaderActions>
          <Tooltip title="Limpar conversa">
            <span>
              <IconButton
                aria-label="Limpar conversa"
                disabled={
                  clearing || loading || (messages.length === 1 && !input)
                }
                onClick={handleClear}
              >
                {clearing ? <CircularProgress size={20} /> : <DeleteOutline />}
              </IconButton>
            </span>
          </Tooltip>

          <IconButton aria-label="Fechar assistente" onClick={onClose}>
            <Close />
          </IconButton>
        </S.HeaderActions>
      </S.Header>

      <S.Messages ref={messagesContainerRef} aria-live="polite">
        {messages.map((message) => (
          <S.Message key={message.id} className={message.role}>
            <S.MessageAvatar className={message.role}>
              {message.role === 'user' ? <Person /> : <Chat />}
            </S.MessageAvatar>
            <S.MessageBubble className={message.role}>
              {renderMessageContent(message.content)}
            </S.MessageBubble>
          </S.Message>
        ))}

        {loading && (
          <S.Message>
            <S.MessageAvatar>
              <Chat />
            </S.MessageAvatar>
            <S.MessageBubble>
              <S.LoadingContent>
                <CircularProgress size={16} />
                <span>Preparando resposta...</span>
              </S.LoadingContent>
            </S.MessageBubble>
          </S.Message>
        )}
      </S.Messages>

      <S.Composer onSubmit={handleSend}>
        <div>
          <TextField
            fullWidth
            multiline
            rowsMax={5}
            value={input}
            variant="outlined"
            placeholder="Digite sua pergunta..."
            disabled={loading}
            inputProps={{ 'aria-label': 'Mensagem para o assistente' }}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <IconButton
            type="submit"
            color="primary"
            aria-label="Enviar mensagem"
            disabled={!input.trim() || loading}
          >
            <Send />
          </IconButton>
        </div>
        <S.ComposerHint>
          Enter envia • Shift + Enter quebra a linha
        </S.ComposerHint>
      </S.Composer>
    </S.Container>
  );
}

AssistantChat.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
